<template>
  <div class="flex flex-col gap-y-3 items-start justify-start w-1/4 relative sidebar overflow-y-auto content-scroll pr-1 pl-6 py-6">
    <div v-permissions="getCompanySectionPermissions" class="flex flex-col gap-y-2 items-start justify-start self-stretch shrink-0 relative">
      <WcTypography :title="$t('recruiterSettings.company')" size="xs" weight="600" color="#010113" class="relative self-stretch" />
      <div class="flex flex-col items-start justify-start self-stretch shrink-0 relative gap-y-1">
        <LinkButton
          v-permissions="getCareerBrandPagePermissions"
          :link="{
            name: $t('recruiterSettings.companyBrand'),
            to: `/recruiter-settings/brands`,
            active: 'brands'
          }"
        />
        <LinkButton
          v-permissions="getTeamsPagePermissions"
          data-testid="team-members" :link="{
          name: $t('recruiterSettings.teamMembers'),
          to: `/recruiter-settings/teams`,
          active: 'teams'
        }"
        />
        <LinkButton
          v-permissions="getLocationAndDepartmentPermissions"
          data-testid="company-locations" :link="{
          name: $t('recruiterSettings.locationAndDepartments'),
          to: `/recruiter-settings/locations-departments`,
          active: 'locations-departments'
        }"
        />
        <LinkButton
          v-if="CompanyModule().haveCvParseAndScoreSupport"
          v-permissions="getCompanyFeatureCreditsPermissions"
          data-testid="ai-features-and-credits" :link="{
          name: $t('recruiterSettings.aiFeaturesAndCredits'),
          to: `/recruiter-settings/ai-features-and-credits`,
          active: 'ai-features-and-credits'
         }"
        />
      </div>
    </div>
    <div v-permissions="getRecruitingPermissions" class="flex flex-col gap-y-2 items-start justify-start self-stretch shrink-0 relative">
      <WcTypography :title="$t('recruiterSettings.recruiting')" size="xs" weight="600" color="#010113" class="relative self-stretch" />
      <div class="flex flex-col items-start justify-start self-stretch shrink-0 relative gap-y-1">
        <LinkButton
          v-permissions="stagePipelineArchivalReasonPermissions"
          :link="{
          name: $t('recruiterSettings.defaultPipelineAndArchivalReasons'),
          to: `/recruiter-settings/stage-pipeline`,
          active: 'stage-pipeline'
        }"
        />
        <LinkButton
          v-permissions="getInterviewFeedbackTemplateManagePermissions"
          :link="{
            name: $t('recruiterSettings.interviewScorecardTemplates'),
            to: `/recruiter-settings/interview-scorecard-templates`,
            active: 'interview-scorecard-templates'
          }"
        />
        <LinkButton
          v-permissions="getCompanyEmailTemplateManagePermissions"
          :link="{
          name: $t('recruiterSettings.emailTemplates'),
          to: `/recruiter-settings/manage-emails`,
          active: 'manage-emails'
          }"
        />
        <LinkButton
          v-permissions="getOfferLetterTemplateManagePermissions"
          :link="{
          name: $t('listOffers.title'),
          to: `/recruiter-settings/offers`,
          active: 'offers'
          }"
        />
        <LinkButton
          v-permissions="getCompanyHrmsIntegrationManagePermissions"
          :link="{
          name: $t('recruiterSettings.integrations'),
          to: `/recruiter-settings/company-integrations`,
          active: 'company-integrations'
          }"
        />
        <LinkButton
          v-permissions="getAgenciesManagePermissions"
          :link="{
          name: $t('recruiterSettings.agencies'),
          to: `/recruiter-settings/agencies`,
          active: 'agencies'
          }"
        />
        <LinkButton
          v-if="CompanyModule().hasRequisitionsFeature"
          v-permissions="getCompanyManageHiringPlanPermissions"
          :link="{
          name: $t('recruiterSettings.hiringPlan'),
          to: manageHiringPlanRoute,
          active: 'manage-hiring-plan'
          }"
        />
      </div>
    </div>
    <div class="flex flex-col gap-y-2 items-start justify-start self-stretch shrink-0 relative">
      <WcTypography :title="$t('recruiterSettings.personal')" size="xs" weight="600" color="#010113" class="relative self-stretch" />
      <div class="flex flex-col items-start justify-start self-stretch shrink-0 relative gap-y-1">
        <LinkButton
          :link="{
            name: $t('recruiterSettings.profile'),
            to: `/recruiter-settings/profile`,
            active: 'profile'
          }"
        />
        <LinkButton
          v-permissions="getCalendarEmailIntegrationsManagePermissions"
          :link="{
            name: $t('recruiterSettings.calendarAndEmailIntegration'),
            to: `/recruiter-settings/app-integration`,
            active: 'app-integration'
          }"
        />
        <LinkButton
          v-permissions="getCompanyBookingLinksManage"
          :link="{
            name: $t('recruiterSettings.interviewAvailability'),
            to: `/recruiter-settings/interview-availability`,
            active: 'interview-availability'
          }"
        />
        <LinkButton
          v-permissions="getNotificationPermissions"
          :link="{
            name: $t('recruiterSettings.notificationPreferences'),
            to: `/recruiter-settings/notification-preferences`,
            active: 'notification-preferences'
          }"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { UserModule } from '@/store/modules/user'
import { CompanyModule } from '@/store/modules/company'
import {
  CAREER_BRAND_PAGE_PERMISSIONS,
  COMPANY_AGENCY_MANAGE_PERMISSIONS,
  COMPANY_ARCHIVAL_REASONS_MANAGE_PERMISSIONS,
  COMPANY_BOOKING_LINKS_MANAGE,
  COMPANY_CREDITS_MANAGE,
  COMPANY_EMAIL_TEMPLATES_MANAGE,
  COMPANY_HRMS_INTEGRATIONS_MANAGE,
  COMPANY_INTERVIEW_FEEDBACK_TEMPLATE_MANAGE,
  COMPANY_LOCATIONS_DEPARTMENTS_MANAGE,
  COMPANY_OFFER_LETTER_TEMPLATE_MANAGE,
  COMPANY_STAGE_PIPELINE_MANAGE_PERMISSIONS,
  COMPANY_TEAM_MEMBERS_MANAGE,
  USER_CALENDAR_EMAIL_INTEGRATIONS_MANAGE,
  USER_NOTIFICATIONS_MANAGE
} from '@/permissions/permissions-constants'
import { PermissionsEnum } from '@/permissions/permissions-enum'
import { manageHiringPlanRoute } from '@/routes/urls'
import WcTypography from '@/components/UILibrary/Typography/WcTypography.vue'

export default {
  name: 'RecruiterSettings',
  components: { WcTypography },
  computed: {
    manageHiringPlanRoute() {
      return manageHiringPlanRoute
    },
    PermissionsEnum() {
      return PermissionsEnum
    },
    getCareerBrandPagePermissions() {
      return CAREER_BRAND_PAGE_PERMISSIONS
    },
    getTeamsPagePermissions() {
      return COMPANY_TEAM_MEMBERS_MANAGE
    },
    getLocationAndDepartmentPermissions() {
      return COMPANY_LOCATIONS_DEPARTMENTS_MANAGE
    },
    getCompanySectionPermissions() {
      return [
        ...CAREER_BRAND_PAGE_PERMISSIONS,
        ...COMPANY_TEAM_MEMBERS_MANAGE,
        ...COMPANY_LOCATIONS_DEPARTMENTS_MANAGE
      ]
    },
    stagePipelineArchivalReasonPermissions() {
      return [...COMPANY_STAGE_PIPELINE_MANAGE_PERMISSIONS, ...COMPANY_ARCHIVAL_REASONS_MANAGE_PERMISSIONS]
    },
    getInterviewFeedbackTemplateManagePermissions() {
      return COMPANY_INTERVIEW_FEEDBACK_TEMPLATE_MANAGE
    },
    getOfferLetterTemplateManagePermissions() {
      return COMPANY_OFFER_LETTER_TEMPLATE_MANAGE
    },
    getCompanyEmailTemplateManagePermissions() {
      return COMPANY_EMAIL_TEMPLATES_MANAGE
    },
    getAgenciesManagePermissions() {
      return COMPANY_AGENCY_MANAGE_PERMISSIONS
    },
    getNotificationPermissions() {
      return USER_NOTIFICATIONS_MANAGE
    },
    getCompanyBookingLinkManagePermissions() {
      return COMPANY_BOOKING_LINKS_MANAGE
    },
    getCompanyHrmsIntegrationManagePermissions() {
      return COMPANY_HRMS_INTEGRATIONS_MANAGE
    },
    getCalendarEmailIntegrationsManagePermissions() {
      return USER_CALENDAR_EMAIL_INTEGRATIONS_MANAGE
    },
    getRecruitingPermissions() {
      return [
        ...this.stagePipelineArchivalReasonPermissions,
        ...this.getCompanyBookingLinkManagePermissions,
        ...this.getInterviewFeedbackTemplateManagePermissions,
        ...this.getCompanyEmailTemplateManagePermissions,
        ...this.getOfferLetterTemplateManagePermissions,
        ...this.getCompanyHrmsIntegrationManagePermissions,
        ...this.getAgenciesManagePermissions
      ]
    },
    getCompanyBookingLinksManage() {
      return COMPANY_BOOKING_LINKS_MANAGE
    },
    getCompanyManageHiringPlanPermissions() {
      return [PermissionsEnum.COMPANY_MANAGE_HIRING_PLAN]
    },
    getCompanyFeatureCreditsPermissions() {
      return COMPANY_CREDITS_MANAGE
    }
  },
  props: {},
  data() {
    return {
      companyId: ''
    }
  },
  mounted() {
    this.initSidebar()
  },
  methods: {
    CompanyModule() {
      return CompanyModule
    },
    UserModule() {
      return UserModule
    },
    initSidebar() {
      this.companyId = UserModule.companyId || ''
    }
  }
}
</script>
<style lang="scss">
.sidebar{
  min-width: 20rem;
}
</style>
